/**
 * A styled button or link (same style, different html for accessibility)
 * @param {boolean} isLink determines whether element is an anchor tag or button
 * @param {string} href an external url
 * @param {jsx} icon an svg imported as a react component
 * @param {string} textColorClass this should be a string for a text color, such as `text-evvy-black`, since in tailwind, the stroke of the text is referenced with `currentColor`.
 * @param {string} bgColorClass this should be a string for a background color, such as `bg-evvy-black`, since in tailwind, the fill of the background is referenced with `currentColor`.
 * @param {string} text a string to label button/link
 * @param {boolean} fullWidth determines whether button should take full width of it's parent container
 * @param {boolean} noLeftMargin sets left margin to 0 when true
 * @param {*} onClick click handler for button
 * @returns jsx of a button or anchor tag
 */
import React from "react";

import { ReactComponent as Checkmark } from "@/components/common/icons/checkmark.svg";
import { cn } from "@/utils/cn";

// simple rectangular button component
/** @deprecated Use `CTA` from `@/components/ui`. Tracked in EVY-437. */
const RectangularButton = ({
  isLink = false,
  href = "",
  text,
  textColorClass = "text-white",
  bgColorClass = "bg-evvy-blue",
  target = "_blank",
  fullWidth = false,
  noLeftMargin = false,
  onClick,
  disabled = false,
  buttonType = "button",
  verticalPadding = "py-4",
  horizontalPadding = "px-10",
  borderColor = "",
  icon = "",
}) => {
  return (
    <>
      {isLink ? (
        <a
          href={href}
          target={target}
          className={cn(
            "t4 ml-0 w-full mt-2 rounded-sm font-semibold tracking-wider text-center",
            "sm:mt-0",
            fullWidth || noLeftMargin ? "sm:ml-0" : "sm:ml-4",
            fullWidth ? "sm:w-full" : "sm:w-auto",
            verticalPadding,
            horizontalPadding,
            bgColorClass,
            textColorClass,
            borderColor && `border ${borderColor}`,
            disabled ? "opacity-50 cursor-not-allowed !pointer-events-auto" : ""
          )}
          onClick={onClick ? onClick : null}
          disabled={disabled}
        >
          {text}
        </a>
      ) : (
        <button
          className={cn(
            "rectangular t4 ml-0 w-full mt-2 rounded-sm font-semibold tracking-wider",
            "sm:mt-0",
            fullWidth || noLeftMargin ? "sm:ml-0" : "sm:ml-4",
            fullWidth ? "sm:w-full" : "sm:w-auto",
            verticalPadding,
            horizontalPadding,
            bgColorClass,
            textColorClass,
            borderColor && `border ${borderColor}`,
            disabled
              ? "opacity-50 cursor-not-allowed !pointer-events-auto"
              : "pointer"
          )}
          disabled={disabled}
          onClick={onClick ? onClick : null}
          type={buttonType}
        >
          <div className="w-full flex items-center align-center justify-center pointer">
            {icon && (
              <span className="flex my-auto">
                <Checkmark className="h-5 w-5 mr-1" />
              </span>
            )}
            {text}
          </div>
        </button>
      )}
    </>
  );
};

export default RectangularButton;
